import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NgbTooltip, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { ShareButtons } from 'ngx-sharebuttons/buttons';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faFacebookF,
  faXTwitter,
  faWhatsapp,
  faFacebookMessenger,
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';

/**
 * Modal component for sharing content via various social platforms.
 */
@Component({
  selector: 'app-share-modal',
  imports: [MatDialogModule, TranslateModule, NgbTooltipModule, ShareButtons],
  templateUrl: './share-modal.html',
  styleUrl: './share-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareModal {
  /**
   * Reference to the dialog instance.
   */
  public readonly dialogRef = inject(MatDialogRef<ShareModal>);

  /**
   * FontAwesome icon library instance.
   */
  private readonly library = inject(FaIconLibrary);

  /**
   * Signal holding the URL to be shared.
   */
  protected readonly url = signal<string>('');

  /**
   * Initializes the share modal, sets the URL to the current location,
   * and adds required icons to the FontAwesome library.
   */
  constructor() {
    this.url.set(window.location.href);

    // Add the required icons to the library
    this.library.addIcons(faFacebookF, faFacebookMessenger, faWhatsapp, faXTwitter, faEnvelope);
  }

  /**
   * Copies the share link to the clipboard and shows a tooltip for feedback.
   * The tooltip is automatically closed after 2 seconds.
   * @param tooltip Tooltip instance to show feedback.
   */
  protected async linkCopy(tooltip: NgbTooltip): Promise<void> {
    await navigator.clipboard.writeText(this.url());
    tooltip.open();
    setTimeout(() => tooltip.close(), 2000);
  }
}
